/**
 * DeleteServiceDialog — confirmation dialog for deleting a service.
 * Wraps the reusable ConfirmDialog with delete-specific logic.
 * Implements mockup SC6.
 */
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { useDeleteService } from '@/api/service-catalog'
import { toast } from 'sonner'

export interface DeleteServiceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  serviceName: string
  onSuccess?: () => void
}

export function DeleteServiceDialog({
  open,
  onOpenChange,
  serviceName,
  onSuccess,
}: DeleteServiceDialogProps) {
  const deleteMutation = useDeleteService()

  async function handleConfirm() {
    try {
      await deleteMutation.mutateAsync(serviceName)
      toast.success(`Service "${serviceName}" deleted`)
      onSuccess?.()
      onOpenChange(false)
    } catch {
      toast.error(`Failed to delete "${serviceName}"`)
    }
  }

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Delete Service?"
      description={`Are you sure you want to delete "${serviceName}"? This will remove the service from the catalog and semantic search. This action cannot be undone.`}
      confirmLabel="Delete"
      onConfirm={handleConfirm}
      variant="destructive"
      isLoading={deleteMutation.isPending}
    />
  )
}
